import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import NotificationBell from '../components/NotificationBell';
import PaymentReminder from '../components/PaymentReminder';
import {
  Activity, LayoutDashboard, Users, ClipboardList, Calendar,
  MessageSquare, LogOut, Menu, X, Settings, UserCog
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import api from '../lib/api';

export default function DoctorLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [doctorBranding, setDoctorBranding] = useState(null);

  const navigation = [
    { name: 'Dashboard', href: '/doctor', icon: LayoutDashboard },
    { name: 'Pacientes', href: '/doctor/patients', icon: Users },
    { name: 'Consultas', href: '/doctor/consultations', icon: ClipboardList },
    { name: 'Citas', href: '/doctor/appointments', icon: Calendar },
    { name: 'Mis Enfermeras', href: '/doctor/my-nurses', icon: UserCog },
    { name: 'Mensajes', href: '/doctor/messages', icon: MessageSquare },
    { name: 'Configuración', href: '/doctor/settings', icon: Settings },
  ];

  useEffect(() => {
    fetchBranding();
  }, []);

  // Refresh branding when navigating away from settings
  useEffect(() => {
    if (location.pathname !== '/doctor/settings') {
      fetchBranding();
    }
  }, [location.pathname]);

  const fetchBranding = async () => {
    try {
      const response = await api.get('/doctors/my-branding');
      setDoctorBranding(response.data);
    } catch (error) {
      // Silently fail - will use defaults
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const primaryColor = doctorBranding?.primary_color || '#2563eb';

  return (
    <div className="min-h-screen flex bg-background">
      <aside
        className={`${
          sidebarOpen ? 'w-64' : 'w-0'
        } bg-slate-900 text-white transition-all duration-300 overflow-hidden flex flex-col`}
        data-testid="sidebar"
      >
        <div className="p-6 flex items-center gap-3 border-b border-slate-800">
          {doctorBranding?.logo_square ? (
            <div className="w-10 h-10 rounded-lg overflow-hidden flex items-center justify-center bg-white">
              <img src={doctorBranding.logo_square} alt="Logo" className="w-full h-full object-contain" />
            </div>
          ) : (
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
              <Activity className="w-6 h-6" />
            </div>
          )}
          <div>
            <h2 className="font-bold text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {doctorBranding?.business_name || 'ClinIA Perú'}
            </h2>
            <p className="text-xs text-slate-400">Médico</p>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`sidebar-nav-item flex items-center gap-3 px-4 py-3 rounded-md text-sm ${
                  isActive ? 'active bg-slate-800' : 'hover:bg-slate-800'
                }`}
                style={isActive ? { backgroundColor: `${primaryColor}22` } : undefined}
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" style={isActive ? { color: primaryColor } : undefined} />
                {item.name}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-800">
          <Button
            variant="ghost"
            className="w-full justify-start text-white hover:bg-slate-800 hover:text-white"
            onClick={logout}
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col">
        <header className="sticky top-0 z-10 backdrop-blur-md bg-white/80 border-b border-border">
          <div className="flex items-center justify-between px-6 py-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="toggle-sidebar-button"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>

            <div className="flex items-center gap-4">
              <NotificationBell />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-button">
                    <Avatar className="w-8 h-8">
                      {doctorBranding?.logo_square && (
                        <AvatarImage src={doctorBranding.logo_square} alt="Logo" />
                      )}
                      <AvatarFallback className="text-white" style={{ backgroundColor: primaryColor }}>
                        {getInitials(user?.full_name || 'DR')}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden md:inline text-sm font-medium">{user?.full_name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Mi Cuenta</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/doctor/settings')} data-testid="settings-menu-item">
                    <Settings className="w-4 h-4 mr-2" />
                    Configuración
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="logout-menu-item">
                    <LogOut className="w-4 h-4 mr-2" />
                    Cerrar Sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-6" data-testid="main-content">
          <div className="max-w-7xl mx-auto">
            <PaymentReminder />
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
